import React from 'react'
import Container from 'react-bootstrap/Container'
import { dehydrate, QueryClient } from 'react-query'
import { getCategoriesBySlug, getLegalDocs } from 'src/@shared/service/cms-api'
import useBlogPage from '@modules/blog/hooks/useBlogPage'
import BlogHeroImage from '@assets/img/blog-hero.webp'
import { Col, Image, Row } from 'react-bootstrap'
import newBlockchain from '@assets/img/first-blockchain.webp'
import BusinessSpeed from '@modules/blog/components/BusinessSpeed/BusinessSpeed'

import { BadgeList } from '@atk/components/ui'
import CustomHead from '@atk/components/head/customHead/customHead'
import ContentFullScreen2 from '@atk/components/content/ContentFullScreen2/ContentFullScreen2'
import Newsletter2 from '@atk/components/newsletter/Newsletter2/Newsletter2'
import BlogVertical from '@atk/components/blog/BlogVertical/BlogVertical'
import BlogHorizontal from '@atk/components/blog/BlogHorizontal/BlogHorizontal'
import ContentRight from '@atk/components/content/ContentRight/ContentRight'

import FramerMotion from '@atk/hooks/framerMotion'

const initialCatArgs = { sort: 'timeCreated', limit: 0, parent: 'Blog' }

export async function getServerSideProps() {
    const queryClient = new QueryClient()
    // prefetch data on the server
    await Promise.all([
        queryClient.fetchQuery(['legaldocs'], () => getLegalDocs({ sort: 'position', limit: 0, page: 1 })),
        queryClient.fetchQuery(['categories'], () => getCategoriesBySlug(initialCatArgs)),
    ])
    return {
        props: {
            // dehydrate query cache
            dehydratedState: dehydrate(queryClient),
        },
    }
}

export default function Blog() {
    const seoDesc = `Stay informed with the latest updates in blockchain technologies through Casper Labs' "Friendly Blog". Explore articles, trends, & industry developments.`
    const {
        /*         search, */
        catList,
        category,
        featured,
        blogList,
        isLoading,
        hasNextPage,
        fetchNextPage,
        handleBadgeListClick,
        /*         handleSearchChange, */
    } = useBlogPage(initialCatArgs)
    const pageClass = `blogPage overflow-hidden `
    return (
        <div className={pageClass}>
            <CustomHead title="Casper Blog | News, Insights & Updates | Casper Labs" description={seoDesc}>
                <link rel="preload" href={BlogHeroImage.src} as="image" />
            </CustomHead>
            <FramerMotion animation="fadeIn">
                <main>
                    <ContentFullScreen2 customClass="blogHero" bgImage={BlogHeroImage.src}>
                        <div className="header-position">
                            <h1 className="mb-4 header-section-title">
                                The Friendly <span className="text-green">Blog</span>
                            </h1>
                            <Col md={8} lg={10}>
                                <p>
                                    Stay up to date on the latest announcements and news from Casper Labs, along with
                                    the broader Casper ecosystem.
                                </p>
                            </Col>
                        </div>
                    </ContentFullScreen2>
                    <Container>
                        <FramerMotion y={400}>
                            {blogList && blogList.length > 0 && (
                                <section className="pt-lg-7 pb-0">
                                    <BlogVertical
                                        customClass="featuredBlog"
                                        imageUrl={featured.webpThumbnailUrl ?? featured.thumbnailUrl}
                                        halfTitle="Featured Post"
                                        title={featured.title}
                                        datePublished={featured.timePublished}
                                        subtitle={featured.preview}
                                        linkButton={[
                                            {
                                                label: 'Read more',
                                                link: `/blog/${featured.meta.slug}`,
                                                variant: 'outline',
                                            },
                                        ]}
                                    />
                                </section>
                            )}
                        </FramerMotion>
                        <FramerMotion y={450}>
                            <section className="pt-6 pb-0">
                                <Newsletter2
                                    col1={6}
                                    col2={6}
                                    title="Sign up for our newsletter!"
                                    desc="Be the first to hear about major upcoming announcements and partnerships"
                                />
                            </section>
                        </FramerMotion>
                        <FramerMotion y={400}>
                            <section className="recentPosts pt-7">
                                <p className="h5">Recent posts</p>
                                <hr />
                                <Row className="my-5 align-items-center">
                                    <Col md={12} className="mb-4 mb-lg-0">
                                        {catList && catList.length > 0 && (
                                            <BadgeList
                                                list={catList}
                                                activeItem={category}
                                                onClick={(item: string) => handleBadgeListClick(item)}
                                            />
                                        )}
                                    </Col>
                                </Row>

                                <BlogHorizontal
                                    list={blogList}
                                    isLoading={isLoading}
                                    hasNextPage={hasNextPage}
                                    fetchNextPage={fetchNextPage}
                                    date
                                />
                            </section>
                        </FramerMotion>
                    </Container>
                    <FramerMotion y={400}>
                        <BusinessSpeed />
                    </FramerMotion>
                    <ContentRight
                        customClass="py-5 py-lg-5 firstBlockhain"
                        colXl1={5}
                        colXl2={7}
                        title="The First Blockchain Built for Enterprise Adoption"
                        linkButton={{ label: 'Contact Us', link: '/contact?section=contactForm', variant: 'primary' }}
                        ContentRightContent={<Image alt="Casper" src={newBlockchain.src}></Image>}
                    />
                </main>
            </FramerMotion>
        </div>
    )
}
